import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DishCategory, DishTag } from '@teffy/shared-types'
import { api } from '../services/api'
import {
  PageHeader, Card, Button, Badge, Modal,
  Field, Input, Select, EmptyState, ConfirmModal,
} from '../components/ui'

const CATEGORY_LABEL: Record<string, string> = {
  [DishCategory.APPETIZER]: 'Entrada',
  [DishCategory.MAIN]:      'Plato fuerte',
  [DishCategory.DESSERT]:   'Postre',
  [DishCategory.DRINK]:     'Bebida',
  [DishCategory.SIDE]:      'Acompañamiento',
}

const TAG_LABEL: Record<string, string> = {
  [DishTag.VEGAN]:       'Vegano',
  [DishTag.VEGETARIAN]:  'Vegetariano',
  [DishTag.GLUTEN_FREE]: 'Sin gluten',
  [DishTag.SPICY]:       'Picante',
  [DishTag.POPULAR]:     'Popular',
}

interface Ingredient { id: string; name: string; nameEn?: string | null; unit: string; outOfStock: boolean; possibleStates: string[]; possibleStatesEn: string[]; possibleFlavors: string[]; possibleFlavorsEn: string[] }
interface SelectedIngredient { ingredientId: string; quantity: number; required: boolean }
interface DishIngredient { ingredientId: string; ingredient: Ingredient; quantity: number; required: boolean }
interface DishExtra { id?: string; name: string; nameEn?: string; cost: number; available: boolean }
interface DishSide { id?: string; name: string; nameEn?: string }
interface ComboSlotAllowedDish { dishId: string; dish: { id: string; name: string } }
interface ComboSlotData { id: string; name: string; required: boolean; order: number; allowedDishes: ComboSlotAllowedDish[] }
interface ComboSlotDraft { clientId: string; name: string; required: boolean; order: number; dishIds: string[] }
interface Dish {
  id: string; name: string; nameEn?: string | null; description: string; descriptionEn?: string | null; basePrice: number; discount: number
  loyaltyPoints: number; listInTopRated: boolean
  category: DishCategory; tags: DishTag[]; available: boolean
  isCombo: boolean
  imageUrl?: string; assignedToId?: string | null
  eventStock?: number | null
  assignedTo?: { id: string; name: string } | null
  ingredients: DishIngredient[]
  extras: DishExtra[]
  sides: DishSide[]
  sidesAllowance?: number | null
  comboSlots?: ComboSlotData[]
}
interface KitchenStaff { id: string; name: string }
interface Menu { id: string; name: string; active: boolean; _count: { dishes: number } }

type Tab = 'menus' | 'dishes' | 'ingredients'

export default function MenuPage() {
  const [tab, setTab] = useState<Tab>('menus')
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null)

  return (
    <div className="p-8">
      <PageHeader title="Menú" subtitle="Gestiona menús, platillos e ingredientes" />

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        {([['menus', 'Menús'], ['dishes', 'Platillos'], ['ingredients', 'Ingredientes']] as [Tab, string][]).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors
              ${tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'menus' && (
        <MenusTab onSelectMenu={(id) => { setSelectedMenuId(id); setTab('dishes') }} />
      )}
      {tab === 'dishes' && <DishesTab menuId={selectedMenuId} />}
      {tab === 'ingredients' && <IngredientsTab />}
    </div>
  )
}

// ─── Menús ────────────────────────────────────────────────────────────────────
function MenusTab({ onSelectMenu }: { onSelectMenu: (id: string) => void }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<Menu | null>(null)
  const [scheduleMenu, setScheduleMenu] = useState<Menu | null>(null)

  const { data: menus = [], isLoading } = useQuery<Menu[]>({
    queryKey: ['menus'],
    queryFn: async () => (await api.get('/menus')).data,
  })

  const { mutate: create, isPending } = useMutation({
    mutationFn: async () => api.post('/menus', { name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['menus'] }); setOpen(false); setName('') },
  })

  const { mutate: toggleActive } = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/menus/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })

  const { mutate: remove } = useMutation({
    mutationFn: async (id: string) => api.delete(`/menus/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menus'] }),
  })

  if (isLoading) return <div className="text-sm text-gray-400">Cargando…</div>

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setOpen(true)}>+ Nuevo menú</Button>
      </div>

      {menus.length === 0
        ? <EmptyState emoji="📋" title="Sin menús" sub="Crea tu primer menú para empezar" />
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {menus.map((m) => (
              <Card key={m.id} className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-gray-900">{m.name}</h3>
                    <p className="text-sm text-gray-400 mt-0.5">{m._count.dishes} platillos</p>
                  </div>
                  <Badge label={m.active ? 'Activo' : 'Inactivo'} color={m.active ? 'green' : 'gray'} />
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <Button size="sm" variant="secondary" onClick={() => onSelectMenu(m.id)}>
                    Ver platillos
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setScheduleMenu(m)}>
                    🕐 Horarios
                  </Button>
                  <Button size="sm" variant="secondary"
                    onClick={() => toggleActive({ id: m.id, active: !m.active })}>
                    {m.active ? 'Desactivar' : 'Activar'}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setConfirmDelete(m)}>
                    Eliminar
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

      <ConfirmModal
        open={!!confirmDelete}
        title={`¿Eliminar menú "${confirmDelete?.name}"?`}
        message={`Este menú contiene ${confirmDelete?._count.dishes ?? 0} platillo(s). Se eliminarán todos junto con sus ingredientes y opciones.`}
        warning="También se borrarán los ítems de órdenes históricas vinculados a estos platillos. Esta acción no se puede deshacer."
        onConfirm={() => { remove(confirmDelete!.id); setConfirmDelete(null) }}
        onCancel={() => setConfirmDelete(null)}
      />

      <Modal title="Nuevo menú" open={open} onClose={() => setOpen(false)}>
        <div className="space-y-4">
          <Field label="Nombre del menú">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Menú principal" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button loading={isPending} disabled={!name.trim()} onClick={() => create()}>Crear</Button>
          </div>
        </div>
      </Modal>

      {scheduleMenu && (
        <ScheduleModal
          title={`Horarios · ${scheduleMenu.name}`}
          menuId={scheduleMenu.id}
          onClose={() => setScheduleMenu(null)}
        />
      )}
    </>
  )
}

// ─── Platillos ────────────────────────────────────────────────────────────────
function DishesTab({ menuId }: { menuId: string | null }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editDish, setEditDish] = useState<Dish | null>(null)
  const [confirmDeleteDish, setConfirmDeleteDish] = useState<Dish | null>(null)
  const [scheduleDish, setScheduleDish] = useState<{ dish: Dish; menuId: string } | null>(null)

  const [form, setForm] = useState({
    name: '', nameEn: '', description: '', descriptionEn: '', basePrice: '', discount: '0',
    loyaltyPoints: '0',
    listInTopRated: false,
    category: DishCategory.MAIN as string, imageUrl: '', assignedToId: '',
    eventStock: '',
  })
  const [selectedTags, setSelectedTags] = useState<DishTag[]>([])

  // Ingredient selector state
  const [selectedIngredients, setSelectedIngredients] = useState<SelectedIngredient[]>([])
  const [pickedIngId, setPickedIngId] = useState('')
  const [pickedQty, setPickedQty] = useState('1')
  const [pickedRequired, setPickedRequired] = useState(false)

  // Inline new ingredient state
  const [showNewIng, setShowNewIng] = useState(false)
  const [newIngForm, setNewIngForm] = useState({ name: '', unit: '' })

  // Extras state
  const [selectedExtras, setSelectedExtras] = useState<DishExtra[]>([])
  const [newExtraForm, setNewExtraForm] = useState({ name: '', nameEn: '', cost: '' })

  // Sides state
  const [sidesAllowance, setSidesAllowance] = useState<string>('')
  const [selectedSides, setSelectedSides] = useState<DishSide[]>([])
  const [newSideForm, setNewSideForm] = useState({ name: '', nameEn: '' })

  // Combo state
  const [isComboMode, setIsComboMode] = useState(false)
  const [comboSlots, setComboSlots] = useState<ComboSlotDraft[]>([])
  const [newSlotName, setNewSlotName] = useState('')
  const [newSlotRequired, setNewSlotRequired] = useState(true)
  const [slotPickDishId, setSlotPickDishId] = useState<Record<string, string>>({})

  const { data: menus = [] } = useQuery<Menu[]>({
    queryKey: ['menus'],
    queryFn: async () => (await api.get('/menus')).data,
  })
  const activeMenuId = menuId ?? menus[0]?.id
  const selectedMenu = menus.find((m) => m.id === activeMenuId)

  const { data: dishes = [], isLoading } = useQuery<Dish[]>({
    queryKey: ['dishes', activeMenuId],
    queryFn: async () => (await api.get(`/menus/${activeMenuId}/dishes`)).data,
    enabled: !!activeMenuId,
  })

  const { data: ingredients = [] } = useQuery<Ingredient[]>({
    queryKey: ['ingredients'],
    queryFn: async () => (await api.get('/menus/ingredients')).data,
  })

  const { data: kitchenStaff = [] } = useQuery<KitchenStaff[]>({
    queryKey: ['staff-kitchen'],
    queryFn: async () => (await api.get('/restaurant/staff?role=KITCHEN')).data,
  })

  // Create ingredient inline and auto-add it to selected list
  const { mutate: createIngredient, isPending: creatingIng } = useMutation({
    mutationFn: async () =>
      api.post('/menus/ingredients', { name: newIngForm.name, unit: newIngForm.unit, possibleStates: [] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingredients'] })
      setNewIngForm({ name: '', unit: '' })
      setShowNewIng(false)
    },
  })

  const { mutate: saveDish, isPending, error: saveError } = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        basePrice: Number(form.basePrice),
        discount: Number(form.discount) || 0,
        loyaltyPoints: Number(form.loyaltyPoints) || 0,
        listInTopRated: form.listInTopRated,
        isCombo: isComboMode,
        tags: selectedTags,
        imageUrl: form.imageUrl.trim() || null,
        assignedToId: form.assignedToId || null,
        eventStock: form.eventStock.trim() !== '' ? Number(form.eventStock) : null,
        ingredients: isComboMode ? [] : selectedIngredients,
        nameEn: form.nameEn.trim() || null,
        descriptionEn: form.descriptionEn.trim() || null,
        extras: selectedExtras.map((e) => ({ name: e.name, nameEn: e.nameEn || null, cost: Number(e.cost), available: e.available })),
        sidesAllowance: sidesAllowance.trim() !== '' ? Number(sidesAllowance) : null,
        sides: selectedSides.map((s) => ({ name: s.name, nameEn: s.nameEn || null })),
        comboSlots: isComboMode
          ? comboSlots.map((s, idx) => ({ name: s.name, required: s.required, order: idx, dishIds: s.dishIds }))
          : [],
      }
      if (editDish) return api.patch(`/menus/${activeMenuId}/dishes/${editDish.id}`, payload)
      return api.post(`/menus/${activeMenuId}/dishes`, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dishes', activeMenuId] })
      closeModal()
    },
  })

  const { mutate: removeDish } = useMutation({
    mutationFn: async (dishId: string) => api.delete(`/menus/${activeMenuId}/dishes/${dishId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dishes', activeMenuId] }),
  })

  const { mutate: toggleAvailable } = useMutation({
    mutationFn: async ({ dishId, available }: { dishId: string; available: boolean }) =>
      api.patch(`/menus/${activeMenuId}/dishes/${dishId}`, { available }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dishes', activeMenuId] }),
  })

  const { mutate: duplicateDish, isPending: duplicating } = useMutation({
    mutationFn: async (dishId: string) =>
      api.post(`/menus/${activeMenuId}/dishes/${dishId}/duplicate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dishes', activeMenuId] }),
  })

  function closeModal() {
    setOpen(false)
    setEditDish(null)
    setForm({ name: '', nameEn: '', description: '', descriptionEn: '', basePrice: '', discount: '0', loyaltyPoints: '0', listInTopRated: false, category: DishCategory.MAIN, imageUrl: '', assignedToId: '', eventStock: '' })
    setSelectedTags([])
    setSelectedIngredients([])
    setPickedIngId('')
    setPickedQty('1')
    setPickedRequired(false)
    setShowNewIng(false)
    setNewIngForm({ name: '', unit: '' })
    setSelectedExtras([])
    setNewExtraForm({ name: '', nameEn: '', cost: '' })
    setSidesAllowance('')
    setSelectedSides([])
    setNewSideForm({ name: '', nameEn: '' })
    setIsComboMode(false)
    setComboSlots([])
    setNewSlotName('')
    setNewSlotRequired(true)
    setSlotPickDishId({})
  }

  function openEdit(dish: Dish) {
    setEditDish(dish)
    setForm({
      name: dish.name,
      nameEn: dish.nameEn ?? '',
      description: dish.description,
      descriptionEn: dish.descriptionEn ?? '',
      basePrice: String(dish.basePrice),
      discount: String(dish.discount ?? 0),
      loyaltyPoints: String(dish.loyaltyPoints ?? 0),
      listInTopRated: dish.listInTopRated ?? false,
      category: dish.category,
      imageUrl: dish.imageUrl ?? '',
      assignedToId: dish.assignedToId ?? '',
      eventStock: dish.eventStock != null ? String(dish.eventStock) : '',
    })
    setSelectedTags(dish.tags)
    setSelectedIngredients(
      dish.ingredients.map((i) => ({
        ingredientId: i.ingredientId,
        quantity: Number(i.quantity),
        required: i.required,
      }))
    )
    setSelectedExtras(dish.extras ?? [])
    setSidesAllowance(dish.sidesAllowance != null ? String(dish.sidesAllowance) : '')
    setSelectedSides(dish.sides ?? [])
    if (dish.isCombo && dish.comboSlots) {
      setIsComboMode(true)
      setComboSlots(dish.comboSlots.map((s, idx) => ({
        clientId: s.id,
        name: s.name,
        required: s.required,
        order: s.order,
        dishIds: s.allowedDishes.map((d) => d.dishId),
      })))
    }
    setOpen(true)
  }

  function toggleTag(tag: DishTag) {
    setSelectedTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag])
  }

  function addIngredient() {
    if (!pickedIngId || Number(pickedQty) <= 0) return
    if (selectedIngredients.some((i) => i.ingredientId === pickedIngId)) return
    setSelectedIngredients((prev) => [
      ...prev,
      { ingredientId: pickedIngId, quantity: Number(pickedQty), required: pickedRequired },
    ])
    setPickedIngId('')
    setPickedQty('1')
    setPickedRequired(false)
  }

  function removeIngredient(ingredientId: string) {
    setSelectedIngredients((prev) => prev.filter((i) => i.ingredientId !== ingredientId))
  }

  const sidesAllowanceNum = sidesAllowance.trim() !== '' ? Number(sidesAllowance) : null
  const canSave = !!form.name && !!form.basePrice &&
    (sidesAllowanceNum === null || selectedSides.length > 0) && (
    isComboMode
      ? comboSlots.length > 0 && comboSlots.every((s) => s.dishIds.length > 0)
      : true
  )
  const availableToAdd = ingredients.filter((i) => !selectedIngredients.some((s) => s.ingredientId === i.id))

  if (!activeMenuId) return <EmptyState emoji="📋" title="Crea un menú primero" />

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-sm text-gray-500">Menú: </span>
          <span className="font-semibold text-gray-900">{selectedMenu?.name}</span>
        </div>
        <Button onClick={() => { setEditDish(null); setOpen(true) }}>+ Nuevo platillo</Button>
      </div>

      {isLoading
        ? <div className="text-sm text-gray-400">Cargando…</div>
        : dishes.length === 0
          ? <EmptyState emoji="🍽" title="Sin platillos" sub="Agrega el primer platillo a este menú" />
          : (
            <div className="space-y-2">
              {dishes.map((d) => (
                <Card key={d.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{d.name}</span>
                        <Badge label={CATEGORY_LABEL[d.category] ?? d.category} color="gray" />
                        {!d.available && <Badge label="No disponible" color="red" />}
                        {d.tags.map((tag) => (
                          <Badge key={tag} label={TAG_LABEL[tag] ?? tag} color="green" />
                        ))}
                      </div>
                      <p className="text-sm text-gray-400 mt-0.5 truncate">{d.description}</p>
                      {d.assignedTo && (
                        <p className="text-xs text-purple-500 mt-0.5">👨‍🍳 {d.assignedTo.name}</p>
                      )}
                      {d.ingredients.length > 0 && (
                        <p className="text-xs text-gray-300 mt-0.5">
                          {d.ingredients.map((i) => i.ingredient.name).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {d.loyaltyPoints > 0 && (
                        <span className="text-xs text-amber-600 font-medium bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
                          ⭐ {d.loyaltyPoints} pts
                        </span>
                      )}
                      {Number(d.discount) > 0 ? (
                        <div className="text-right">
                          <span className="text-xs text-red-500 line-through block">${Number(d.basePrice).toFixed(2)}</span>
                          <span className="font-bold text-green-600">
                            ${(Number(d.basePrice) * (1 - Number(d.discount) / 100)).toFixed(2)}
                            <span className="ml-1 text-xs font-semibold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">-{Number(d.discount)}%</span>
                          </span>
                        </div>
                      ) : (
                        <span className="font-bold text-gray-900">${Number(d.basePrice).toFixed(2)}</span>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)}>Editar</Button>
                      <Button size="sm" variant="secondary" loading={duplicating} onClick={() => duplicateDish(d.id)}>Duplicar</Button>
                      <Button size="sm" variant="secondary" onClick={() => setScheduleDish({ dish: d, menuId: activeMenuId! })}>🕐</Button>
                      <Button size="sm" variant="secondary"
                        onClick={() => toggleAvailable({ dishId: d.id, available: !d.available })}>
                        {d.available ? 'Ocultar' : 'Mostrar'}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => setConfirmDeleteDish(d)}>Eliminar</Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )
      }

      <ConfirmModal
        open={!!confirmDeleteDish}
        title={`¿Eliminar "${confirmDeleteDish?.name}"?`}
        message="Se eliminará el platillo con todas sus opciones e ingredientes."
        warning="Los ítems de órdenes históricas vinculados a este platillo también se borrarán. Esta acción no se puede deshacer."
        onConfirm={() => { removeDish(confirmDeleteDish!.id); setConfirmDeleteDish(null) }}
        onCancel={() => setConfirmDeleteDish(null)}
      />

      <Modal title={editDish ? 'Editar platillo' : 'Nuevo platillo'} open={open} onClose={closeModal}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre (ES)">
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label="Name (EN)">
              <Input value={form.nameEn} placeholder="Optional" onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Descripción (ES)">
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Field>
            <Field label="Description (EN)">
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
                rows={3}
                placeholder="Optional"
                value={form.descriptionEn}
                onChange={(e) => setForm((f) => ({ ...f, descriptionEn: e.target.value }))}
              />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Precio base">
              <Input type="number" min="0" step="0.5" value={form.basePrice}
                onChange={(e) => setForm((f) => ({ ...f, basePrice: e.target.value }))} />
            </Field>
            <Field label="Descuento (%)">
              <Input type="number" min="0" max="100" step="1" value={form.discount}
                onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))}
                placeholder="0" />
              {Number(form.discount) > 0 && Number(form.basePrice) > 0 && (
                <p className="text-xs text-green-600 mt-1">
                  Precio final: ${(Number(form.basePrice) * (1 - Number(form.discount) / 100)).toFixed(2)}
                </p>
              )}
            </Field>
            <Field label="Puntos lealtad">
              <Input type="number" min="0" step="1" value={form.loyaltyPoints}
                onChange={(e) => setForm((f) => ({ ...f, loyaltyPoints: e.target.value }))} />
            </Field>
            <Field label="Stock por evento (opcional)">
              <Input type="number" min="1" step="1" placeholder="Ej: 50" value={form.eventStock}
                onChange={(e) => setForm((f) => ({ ...f, eventStock: e.target.value }))} />
              <p className="text-xs text-gray-400 mt-1">Porciones disponibles en modo evento. Vacío = ilimitado.</p>
            </Field>
            <Field label="">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.listInTopRated}
                  onChange={(e) => setForm((f) => ({ ...f, listInTopRated: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 accent-gray-900"
                />
                <span className="text-sm text-gray-700">Listar en mejor valuados</span>
              </label>
            </Field>
            <Field label="Categoría">
              <Select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                {Object.entries(CATEGORY_LABEL).map(([val, lbl]) => (
                  <option key={val} value={val}>{lbl}</option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="URL de imagen (opcional)">
            <Input value={form.imageUrl} onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))} placeholder="https://…" />
            {saveError && (
              <p className="text-xs text-red-500 mt-1">
                Error: {(saveError as { response?: { data?: { error?: string } } })?.response?.data?.error ?? (saveError as Error).message}
              </p>
            )}
          </Field>
          <Field label="Responsable Cocina/Bar (opcional)">
            <Select
              value={form.assignedToId}
              onChange={(e) => setForm((f) => ({ ...f, assignedToId: e.target.value }))}
            >
              <option value="">— Sin asignar —</option>
              {kitchenStaff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Etiquetas">
            <div className="flex gap-2 flex-wrap mt-1">
              {Object.entries(TAG_LABEL).map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => toggleTag(val as DishTag)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                    ${selectedTags.includes(val as DishTag)
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </Field>

          {/* ── ¿Es un combo? ─────────────────────────────────────────── */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isComboMode}
                onChange={(e) => setIsComboMode(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 accent-amber-500"
              />
              <div>
                <span className="font-semibold text-amber-800 text-sm">¿Es un combo?</span>
                <p className="text-xs text-amber-600 mt-0.5">El comensal elegirá un platillo por cada slot configurado</p>
              </div>
            </label>

            {isComboMode && (
              <div className="mt-4 space-y-3">
                {/* Slots existentes */}
                {comboSlots.map((slot) => {
                  const slotDishes = dishes.filter((d) => slot.dishIds.includes(d.id))
                  const availableForSlot = dishes.filter((d) => !d.isCombo && d.id !== editDish?.id && !slot.dishIds.includes(d.id))
                  return (
                    <div key={slot.clientId} className="bg-white border border-amber-200 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-gray-800">{slot.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${slot.required ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                            {slot.required ? 'Obligatorio' : 'Opcional'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setComboSlots((prev) => prev.filter((s) => s.clientId !== slot.clientId))}
                          className="text-gray-300 hover:text-red-400 font-bold text-sm"
                        >✕</button>
                      </div>
                      {/* Platillos del slot */}
                      <div className="flex flex-wrap gap-1">
                        {slotDishes.map((d) => (
                          <span key={d.id} className="flex items-center gap-1 bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full font-medium">
                            {d.name}
                            <button
                              type="button"
                              onClick={() => setComboSlots((prev) => prev.map((s) => s.clientId === slot.clientId
                                ? { ...s, dishIds: s.dishIds.filter((id) => id !== d.id) }
                                : s
                              ))}
                              className="hover:text-red-500 font-bold ml-0.5"
                            >×</button>
                          </span>
                        ))}
                      </div>
                      {/* Agregar platillo al slot */}
                      {availableForSlot.length > 0 && (
                        <div className="flex gap-2">
                          <Select
                            value={slotPickDishId[slot.clientId] ?? ''}
                            onChange={(e) => setSlotPickDishId((prev) => ({ ...prev, [slot.clientId]: e.target.value }))}
                          >
                            <option value="">+ Agregar platillo…</option>
                            {availableForSlot.map((d) => (
                              <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                          </Select>
                          <Button
                            size="sm"
                            disabled={!slotPickDishId[slot.clientId]}
                            onClick={() => {
                              const dishId = slotPickDishId[slot.clientId]
                              if (!dishId) return
                              setComboSlots((prev) => prev.map((s) => s.clientId === slot.clientId
                                ? { ...s, dishIds: [...s.dishIds, dishId] }
                                : s
                              ))
                              setSlotPickDishId((prev) => ({ ...prev, [slot.clientId]: '' }))
                            }}
                          >Agregar</Button>
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* Agregar nuevo slot */}
                <div className="border border-dashed border-amber-300 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-amber-700">Nuevo slot</p>
                  <div className="flex gap-2 items-center">
                    <Input
                      placeholder="Nombre (ej: Bebida)"
                      value={newSlotName}
                      onChange={(e) => setNewSlotName(e.target.value)}
                    />
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newSlotRequired}
                        onChange={(e) => setNewSlotRequired(e.target.checked)}
                        className="rounded"
                      />
                      Obligatorio
                    </label>
                    <Button
                      size="sm"
                      disabled={!newSlotName.trim()}
                      onClick={() => {
                        setComboSlots((prev) => [...prev, {
                          clientId: `slot-${Date.now()}`,
                          name: newSlotName.trim(),
                          required: newSlotRequired,
                          order: prev.length,
                          dishIds: [],
                        }])
                        setNewSlotName('')
                        setNewSlotRequired(true)
                      }}
                    >Agregar slot</Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Ingredientes ──────────────────────────────────────────── */}
          {!isComboMode && <Field label={`Ingredientes${!editDish ? ' *' : ''}`}>
            {/* Lista de ingredientes seleccionados */}
            {selectedIngredients.length > 0 && (
              <div className="space-y-1 mb-3">
                {selectedIngredients.map((si) => {
                  const ing = ingredients.find((i) => i.id === si.ingredientId)
                  return (
                    <div key={si.ingredientId}
                      className="flex items-center gap-2 bg-gray-50 border border-gray-100 px-3 py-1.5 rounded-lg text-sm">
                      <span className="flex-1 font-medium text-gray-800">{ing?.name}</span>
                      <span className="text-gray-400 text-xs">{si.quantity} {ing?.unit}</span>
                      {si.required && (
                        <span className="text-xs bg-orange-50 text-orange-500 border border-orange-100 px-1.5 py-0.5 rounded">
                          Requerido
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeIngredient(si.ingredientId)}
                        className="text-gray-300 hover:text-red-400 transition-colors font-bold"
                      >
                        ✕
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Selector para agregar ingrediente existente */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Select value={pickedIngId} onChange={(e) => setPickedIngId(e.target.value)}>
                  <option value="">Selecciona ingrediente…</option>
                  {availableToAdd.map((i) => (
                    <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
                  ))}
                </Select>
              </div>
              <div className="w-20">
                <Input
                  type="number" min="0.1" step="0.1" placeholder="Cant."
                  value={pickedQty}
                  onChange={(e) => setPickedQty(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap pb-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pickedRequired}
                  onChange={(e) => setPickedRequired(e.target.checked)}
                  className="rounded"
                />
                Req.
              </label>
              <Button size="sm" disabled={!pickedIngId || !pickedQty} onClick={addIngredient}>
                Agregar
              </Button>
            </div>

            {/* Crear ingrediente nuevo inline */}
            {!showNewIng ? (
              <button
                type="button"
                onClick={() => setShowNewIng(true)}
                className="text-xs text-blue-500 hover:text-blue-700 mt-2 transition-colors"
              >
                + Crear nuevo ingrediente
              </button>
            ) : (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-blue-700">Nuevo ingrediente</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Nombre (ej. Carne de res)"
                    value={newIngForm.name}
                    onChange={(e) => setNewIngForm((f) => ({ ...f, name: e.target.value }))}
                  />
                  <Input
                    placeholder="Unidad (g, ml, pza…)"
                    value={newIngForm.unit}
                    onChange={(e) => setNewIngForm((f) => ({ ...f, unit: e.target.value }))}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    loading={creatingIng}
                    disabled={!newIngForm.name || !newIngForm.unit}
                    onClick={() => createIngredient()}
                  >
                    Crear ingrediente
                  </Button>
                  <Button
                    size="sm" variant="secondary"
                    onClick={() => { setShowNewIng(false); setNewIngForm({ name: '', unit: '' }) }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </Field>}

          {/* ── Guarniciones sin costo ────────────────────────────────── */}
          <Field label="Guarniciones incluidas (opcional)">
            <div className="flex gap-2 items-center mb-3">
              <Input
                type="number" min="1" step="1" placeholder="Cantidad (ej. 2)"
                value={sidesAllowance}
                onChange={(e) => setSidesAllowance(e.target.value)}
                className="w-40"
              />
              <span className="text-sm text-gray-500">guarnición(es) incluida(s)</span>
            </div>
            {sidesAllowance.trim() !== '' && (
              <>
                {selectedSides.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {selectedSides.map((side, idx) => (
                      <div key={idx}
                        className="flex items-center gap-2 bg-orange-50 border border-orange-100 px-3 py-1.5 rounded-lg text-sm">
                        <span className="flex-1 font-medium text-gray-800">{side.name}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedSides((prev) => prev.filter((_, i) => i !== idx))}
                          className="text-gray-300 hover:text-red-400 transition-colors font-bold"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input
                      placeholder="Nombre ES (ej. Arroz)"
                      value={newSideForm.name}
                      onChange={(e) => setNewSideForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Name EN (optional)"
                      value={newSideForm.nameEn}
                      onChange={(e) => setNewSideForm((f) => ({ ...f, nameEn: e.target.value }))}
                    />
                  </div>
                  <Button
                    size="sm"
                    disabled={!newSideForm.name.trim()}
                    onClick={() => {
                      setSelectedSides((prev) => [...prev, { name: newSideForm.name.trim(), nameEn: newSideForm.nameEn.trim() || undefined }])
                      setNewSideForm({ name: '', nameEn: '' })
                    }}
                  >
                    Agregar
                  </Button>
                </div>
                {sidesAllowanceNum !== null && selectedSides.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Agrega al menos una opción de guarnición</p>
                )}
              </>
            )}
          </Field>

          {/* ── Extras con costo ──────────────────────────────────────── */}
          <Field label="Extras con costo (opcional)">
            {selectedExtras.length > 0 && (
              <div className="space-y-1 mb-3">
                {selectedExtras.map((ex, idx) => (
                  <div key={idx}
                    className="flex items-center gap-2 bg-green-50 border border-green-100 px-3 py-1.5 rounded-lg text-sm">
                    <span className="flex-1 font-medium text-gray-800">{ex.name}</span>
                    <span className="text-green-700 font-semibold">${Number(ex.cost).toFixed(2)}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedExtras((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-gray-300 hover:text-red-400 transition-colors font-bold"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Input
                  placeholder="Nombre ES (ej. Queso extra)"
                  value={newExtraForm.name}
                  onChange={(e) => setNewExtraForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Name EN (optional)"
                  value={newExtraForm.nameEn}
                  onChange={(e) => setNewExtraForm((f) => ({ ...f, nameEn: e.target.value }))}
                />
              </div>
              <div className="w-24">
                <Input
                  type="number" min="0" step="0.5" placeholder="Costo"
                  value={newExtraForm.cost}
                  onChange={(e) => setNewExtraForm((f) => ({ ...f, cost: e.target.value }))}
                />
              </div>
              <Button
                size="sm"
                disabled={!newExtraForm.name.trim() || !newExtraForm.cost}
                onClick={() => {
                  setSelectedExtras((prev) => [
                    ...prev,
                    { name: newExtraForm.name.trim(), nameEn: newExtraForm.nameEn.trim() || undefined, cost: Number(newExtraForm.cost), available: true },
                  ])
                  setNewExtraForm({ name: '', nameEn: '', cost: '' })
                }}
              >
                Agregar
              </Button>
            </div>
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeModal}>Cancelar</Button>
            <Button loading={isPending} disabled={!canSave} onClick={() => saveDish()}>
              {editDish ? 'Guardar cambios' : 'Crear platillo'}
            </Button>
          </div>
        </div>
      </Modal>

      {scheduleDish && (
        <ScheduleModal
          title={`Horarios · ${scheduleDish.dish.name}`}
          menuId={scheduleDish.menuId}
          dishId={scheduleDish.dish.id}
          onClose={() => setScheduleDish(null)}
        />
      )}
    </>
  )
}

// ─── Ingredientes ─────────────────────────────────────────────────────────────
function IngredientsTab() {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editItem, setEditItem] = useState<Ingredient | null>(null)
  const [form, setForm] = useState({ name: '', nameEn: '', unit: '', possibleStates: '', possibleStatesEn: '', possibleFlavors: '', possibleFlavorsEn: '' })
  const [confirmDeleteIng, setConfirmDeleteIng] = useState<Ingredient | null>(null)

  const { data: ingredients = [], isLoading } = useQuery<Ingredient[]>({
    queryKey: ['ingredients'],
    queryFn: async () => (await api.get('/menus/ingredients')).data,
  })

  const { mutate: save, isPending } = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name,
        nameEn: form.nameEn.trim() || null,
        unit: form.unit,
        possibleStates: form.possibleStates ? form.possibleStates.split(',').map((s) => s.trim()).filter(Boolean) : [],
        possibleStatesEn: form.possibleStatesEn ? form.possibleStatesEn.split(',').map((s) => s.trim()).filter(Boolean) : [],
        possibleFlavors: form.possibleFlavors ? form.possibleFlavors.split(',').map((s) => s.trim()).filter(Boolean) : [],
        possibleFlavorsEn: form.possibleFlavorsEn ? form.possibleFlavorsEn.split(',').map((s) => s.trim()).filter(Boolean) : [],
      }
      if (editItem) return api.patch(`/menus/ingredients/${editItem.id}`, payload)
      return api.post('/menus/ingredients', payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingredients'] })
      setOpen(false); setEditItem(null)
      setForm({ name: '', nameEn: '', unit: '', possibleStates: '', possibleStatesEn: '', possibleFlavors: '', possibleFlavorsEn: '' })
    },
  })

  const { mutate: remove } = useMutation({
    mutationFn: async (id: string) => api.delete(`/menus/ingredients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingredients'] }),
  })

  const { mutate: toggleStock } = useMutation({
    mutationFn: async (id: string) => api.patch(`/menus/ingredients/${id}/toggle-stock`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingredients'] }),
  })

  function openEdit(ing: Ingredient) {
    setEditItem(ing)
    setForm({
      name: ing.name,
      nameEn: ing.nameEn ?? '',
      unit: ing.unit,
      possibleStates: ing.possibleStates.join(', '),
      possibleStatesEn: ing.possibleStatesEn.join(', '),
      possibleFlavors: ing.possibleFlavors.join(', '),
      possibleFlavorsEn: ing.possibleFlavorsEn.join(', '),
    })
    setOpen(true)
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditItem(null); setOpen(true) }}>+ Nuevo ingrediente</Button>
      </div>

      {isLoading
        ? <div className="text-sm text-gray-400">Cargando…</div>
        : ingredients.length === 0
          ? <EmptyState emoji="🥩" title="Sin ingredientes" sub="Agrega ingredientes para crear platillos" />
          : (
            <div className="space-y-2">
              {ingredients.map((ing) => (
                <Card key={ing.id} className="px-5 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{ing.name}</span>
                        <span className="text-sm text-gray-400">({ing.unit})</span>
                        {ing.outOfStock && (
                          <span className="text-xs font-semibold bg-red-50 text-red-600 border border-red-100 px-2 py-0.5 rounded-full">
                            Agotado
                          </span>
                        )}
                      </div>
                      {ing.possibleStates.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {ing.possibleStates.map((s) => (
                            <span key={s} className="bg-blue-50 text-blue-600 text-xs px-2 py-0.5 rounded-full font-medium">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                      {ing.possibleFlavors.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {ing.possibleFlavors.map((f) => (
                            <span key={f} className="bg-purple-50 text-purple-600 text-xs px-2 py-0.5 rounded-full font-medium">
                              🍬 {f}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(ing)}>Editar</Button>
                      <Button
                        size="sm"
                        variant={ing.outOfStock ? 'secondary' : 'secondary'}
                        onClick={() => toggleStock(ing.id)}
                        className={ing.outOfStock ? 'text-green-700 border-green-200' : 'text-red-600 border-red-200'}
                      >
                        {ing.outOfStock ? 'Reponer' : 'Agotar'}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => setConfirmDeleteIng(ing)}>Eliminar</Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )
      }

      <ConfirmModal
        open={!!confirmDeleteIng}
        title={`¿Eliminar ingrediente "${confirmDeleteIng?.name}"?`}
        message="Se eliminará del catálogo de ingredientes. Los platillos que ya lo tengan asignado pueden verse afectados."
        warning="Esta acción no se puede deshacer."
        onConfirm={() => { remove(confirmDeleteIng!.id); setConfirmDeleteIng(null) }}
        onCancel={() => setConfirmDeleteIng(null)}
      />

      <Modal title={editItem ? 'Editar ingrediente' : 'Nuevo ingrediente'} open={open} onClose={() => { setOpen(false); setEditItem(null) }}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre (ES)">
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Carne de res" />
            </Field>
            <Field label="Name (EN)">
              <Input value={form.nameEn} onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))} placeholder="Beef (optional)" />
            </Field>
          </div>
          <Field label="Unidad">
            <Input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} placeholder="gramos, pieza, ml…" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Estados posibles — ES (coma)">
              <Input
                value={form.possibleStates}
                onChange={(e) => setForm((f) => ({ ...f, possibleStates: e.target.value }))}
                placeholder="bien cocida, término medio"
              />
            </Field>
            <Field label="Possible states — EN (comma)">
              <Input
                value={form.possibleStatesEn}
                onChange={(e) => setForm((f) => ({ ...f, possibleStatesEn: e.target.value }))}
                placeholder="well done, medium, rare"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sabores posibles — ES (coma)">
              <Input
                value={form.possibleFlavors}
                onChange={(e) => setForm((f) => ({ ...f, possibleFlavors: e.target.value }))}
                placeholder="fresa, chocolate, vainilla"
              />
            </Field>
            <Field label="Possible flavors — EN (comma)">
              <Input
                value={form.possibleFlavorsEn}
                onChange={(e) => setForm((f) => ({ ...f, possibleFlavorsEn: e.target.value }))}
                placeholder="strawberry, chocolate, vanilla"
              />
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => { setOpen(false); setEditItem(null) }}>Cancelar</Button>
            <Button loading={isPending} disabled={!form.name || !form.unit} onClick={() => save()}>
              {editItem ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

// ─── Horarios de disponibilidad ───────────────────────────────────────────────

interface Schedule { id: string; daysOfWeek: number[]; startTime: string; endTime: string }

const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

function ScheduleModal({
  title, menuId, dishId, onClose,
}: {
  title: string
  menuId: string
  dishId?: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const queryKey = dishId ? ['schedules', 'dish', dishId] : ['schedules', 'menu', menuId]
  const url = dishId ? `/menus/${menuId}/dishes/${dishId}/schedules` : `/menus/${menuId}/schedules`

  const { data: schedules = [], isLoading } = useQuery<Schedule[]>({
    queryKey,
    queryFn: async () => (await api.get(url)).data,
  })

  const [days, setDays] = useState<number[]>([])
  const [startTime, setStartTime] = useState('09:00')
  const [endTime, setEndTime] = useState('22:00')

  const { mutate: add, isPending: adding } = useMutation({
    mutationFn: async () => api.post(url, { daysOfWeek: days, startTime, endTime }),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setDays([]) },
  })

  const { mutate: remove } = useMutation({
    mutationFn: async (id: string) => api.delete(`${url}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  })

  function toggleDay(d: number) {
    setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d])
  }

  return (
    <Modal title={title} open onClose={onClose}>
      <div className="space-y-4">
        <p className="text-xs text-gray-400">
          Si no hay horarios configurados, el menú o platillo estará disponible siempre. Con horarios, solo aparece durante los rangos definidos.
        </p>

        {isLoading ? (
          <div className="text-sm text-gray-400">Cargando…</div>
        ) : schedules.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Sin restricciones de horario.</p>
        ) : (
          <div className="space-y-2">
            {schedules.map((s) => (
              <div key={s.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                <div>
                  <div className="flex gap-1 flex-wrap">
                    {s.daysOfWeek.sort().map((d) => (
                      <span key={d} className="bg-gray-900 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                        {DAY_LABELS[d]}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{s.startTime} – {s.endTime}</p>
                </div>
                <button onClick={() => remove(s.id)} className="text-red-500 text-xs font-medium hover:underline ml-4">
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agregar horario</p>
          <div>
            <p className="text-xs text-gray-500 mb-1.5">Días de la semana</p>
            <div className="flex gap-1 flex-wrap">
              {DAY_LABELS.map((label, idx) => (
                <button
                  key={idx}
                  onClick={() => toggleDay(idx)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                    days.includes(idx)
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <p className="text-xs text-gray-500 mb-1">Desde</p>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500 mb-1">Hasta</p>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Cerrar</Button>
            <Button loading={adding} disabled={days.length === 0 || !startTime || !endTime} onClick={() => add()}>
              Agregar horario
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
